import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { SplashScreen } from "@/components/SplashScreen";
import { Translator } from "@/components/Translator";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import HomePage from "@/pages/HomePage";
import SpacesPage from "@/pages/SpacesPage";
import CalendarPage from "@/pages/CalendarPage";
import ShoppingPage from "@/pages/ShoppingPage";
import WishlistPage from "@/pages/WishlistPage";
import SpacePage from "@/pages/SpacePage";
import SpaceSettingsPage from "@/pages/SpaceSettingsPage";
import ProjectPage from "@/pages/ProjectPage";
import FilesPage from "@/pages/FilesPage";
import PhotosPage from "@/pages/PhotosPage";
import ChatPage from "@/pages/ChatPage";
import PollsPage from "@/pages/PollsPage";
import RealEstatePage from "@/pages/RealEstatePage";
import PropertyPage from "@/pages/PropertyPage";
import PropertyDossierPage from "@/pages/PropertyDossierPage";
import ExpensesPage from "@/pages/ExpensesPage";
import MyTasksPage from "@/pages/MyTasksPage";
import ActivityPage from "@/pages/ActivityPage";
import InboxPage from "@/pages/InboxPage";
import ProfilePage from "@/pages/ProfilePage";
import InvitePage from "@/pages/InvitePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import AdminPage from "@/pages/AdminPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentReturnPage";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <SplashScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  return <AppShell>{children}</AppShell>;
};

const AuthRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <SplashScreen />;
  if (user) return <Navigate to="/" replace />;
  return <AuthPage />;
};

function AppRouter() {
  // Detección reactiva del session_id de Emergent Google Auth (antes de montar rutas protegidas)
  const location = useLocation();
  if (location.hash?.includes("session_id=") || window.location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
            <Route path="/auth" element={<AuthRoute />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/" element={<Protected><HomePage /></Protected>} />
            <Route path="/spaces" element={<Protected><SpacesPage /></Protected>} />
            <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
            <Route path="/space/:id" element={<Protected><SpacePage /></Protected>} />
            <Route path="/space/:id/settings" element={<Protected><SpaceSettingsPage /></Protected>} />
            <Route path="/space/:id/project/:projectId" element={<Protected><ProjectPage /></Protected>} />
            <Route path="/space/:id/files" element={<Protected><FilesPage /></Protected>} />
            <Route path="/space/:id/photos" element={<Protected><PhotosPage /></Protected>} />
            <Route path="/space/:id/chat" element={<Protected><ChatPage /></Protected>} />
            <Route path="/space/:id/polls" element={<Protected><PollsPage /></Protected>} />
            <Route path="/space/:id/realestate" element={<Protected><RealEstatePage /></Protected>} />
            <Route path="/space/:id/realestate/:propertyId" element={<Protected><PropertyPage /></Protected>} />
            <Route path="/space/:id/realestate/:propertyId/dossier" element={<Protected><PropertyDossierPage /></Protected>} />
            <Route path="/space/:id/expenses" element={<Protected><ExpensesPage /></Protected>} />
            <Route path="/space/:id/shopping" element={<Protected><ShoppingPage /></Protected>} />
            <Route path="/wishlist" element={<Protected><WishlistPage /></Protected>} />
            <Route path="/my-tasks" element={<Protected><MyTasksPage /></Protected>} />
            <Route path="/space/:id/activity" element={<Protected><ActivityPage /></Protected>} />
            <Route path="/inbox" element={<Protected><InboxPage /></Protected>} />
            <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
            <Route path="/subscription" element={<Protected><SubscriptionPage /></Protected>} />
            <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentSuccessPage /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentCancelPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Translator />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
